/**
 * src/services/validationService.js
 * Servicio centralizado de validaciones
 */

/**
 * Valida un DNI argentino
 * @param {string} dni - DNI a validar
 * @returns {Object} { valido: boolean, error: string|null, dniLimpio: string }
 */
export const validarDNI = (dni) => {
  if (!dni) {
    return { valido: false, error: "El DNI es requerido", dniLimpio: null };
  }

  // Remover espacios, puntos y guiones
  const limpiar = String(dni).replace(/\D/g, '').trim();
  
  // Debe tener exactamente 8 dígitos
  if (limpiar.length !== 8) {
    return { 
      valido: false, 
      error: `El DNI debe tener exactamente 8 dígitos. Ingresaste ${limpiar.length}.`, 
      dniLimpio: limpiar 
    };
  }
  
  // Validar que sean solo números
  if (!/^\d{8}$/.test(limpiar)) {
    return { 
      valido: false, 
      error: "El DNI solo puede contener números", 
      dniLimpio: null 
    };
  }
  
  // Validar que no sea todo ceros o secuencias
  if (/^0+$/.test(limpiar)) {
    return { 
      valido: false, 
      error: "El DNI no puede ser solo ceros", 
      dniLimpio: null 
    };
  }
  
  return { 
    valido: true, 
    error: null, 
    dniLimpio: limpiar 
  };
};

/**
 * Valida un email
 * @param {string} email - Email a validar
 * @returns {Object} { valido: boolean, error: string|null }
 */
export const validarEmail = (email) => {
  if (!email) {
    return { valido: false, error: "El email es requerido" };
  }

  const regexEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!regexEmail.test(email)) {
    return { 
      valido: false, 
      error: "El formato del email no es válido" 
    };
  }

  return { valido: true, error: null };
};

/**
 * Valida un número telefónico
 * @param {string} telefono - Teléfono a validar
 * @returns {Object} { valido: boolean, error: string|null }
 */
export const validarTelefono = (telefono) => {
  if (!telefono) {
    return { valido: false, error: "El teléfono es requerido" };
  }

  // Remover caracteres especiales, espacios, etc.
  const limpiar = String(telefono).replace(/\D/g, '');
  
  // Un teléfono argentino debe tener al menos 10 dígitos
  if (limpiar.length < 10) {
    return { 
      valido: false, 
      error: `El teléfono debe tener al menos 10 dígitos. Ingresaste ${limpiar.length}.` 
    };
  }

  if (limpiar.length > 15) {
    return { 
      valido: false, 
      error: "El teléfono tiene demasiados dígitos" 
    };
  }

  return { valido: true, error: null };
};

/**
 * Valida un monto (dinero)
 * @param {string|number} monto - Monto a validar
 * @param {number} minimo - Monto mínimo (opcional)
 * @param {number} maximo - Monto máximo (opcional)
 * @returns {Object} { valido: boolean, error: string|null }
 */
export const validarMonto = (monto, minimo = 0, maximo = Infinity) => {
  if (!monto) {
    return { valido: false, error: "El monto es requerido" };
  }

  const montoParsed = parseFloat(String(monto).replace(/\D/g, ''));
  
  if (isNaN(montoParsed)) {
    return { 
      valido: false, 
      error: "El monto debe ser un número válido" 
    };
  }

  if (montoParsed < minimo) {
    return { 
      valido: false, 
      error: `El monto mínimo es $${minimo.toLocaleString('es-AR')}` 
    };
  }

  if (montoParsed > maximo) {
    return { 
      valido: false, 
      error: `El monto máximo es $${maximo.toLocaleString('es-AR')}` 
    };
  }

  return { valido: true, error: null };
};

/**
 * Valida que un campo requerido no esté vacío
 * @param {string} valor - Valor a validar
 * @param {string} nombreCampo - Nombre del campo para el mensaje
 * @returns {Object} { valido: boolean, error: string|null }
 */
export const validarRequerido = (valor, nombreCampo = "Este campo") => {
  if (!valor || String(valor).trim() === "") {
    return { 
      valido: false, 
      error: `${nombreCampo} es requerido` 
    };
  }

  return { valido: true, error: null };
};

/**
 * Ejecuta múltiples validaciones y retorna todos los errores
 * @param {Object} validaciones - Objeto con { campo: función de validación }
 * @returns {Object} { todosValidos: boolean, errores: Object }
 */
export const validarFormulario = (validaciones) => {
  const errores = {};
  let todosValidos = true;

  for (const [campo, validar] of Object.entries(validaciones)) {
    const resultado = validar();
    if (!resultado.valido) {
      errores[campo] = resultado.error;
      todosValidos = false;
    }
  }

  return { todosValidos, errores };
};
